import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import Breadcrumbs, { breadcrumbsClasses } from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Link as RouterLink, useLocation } from "react-router";

import { crumbsFor } from "./navigation";

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  [`& .${breadcrumbsClasses.separator}`]: { color: (theme.vars ?? theme).palette.text.disabled, marginInline: 2 },
  [`& .${breadcrumbsClasses.ol}`]: { alignItems: "center", flexWrap: "nowrap" },
  [`& .${breadcrumbsClasses.li}`]: { minWidth: 0 },
}));

const DetailContext = createContext<(label: string | null) => void>(() => undefined);

export function CrumbProvider({ children }: { children: ReactNode }) {
  // 详情标签与路径绑定：换页后旧标签自动失效，不需要在副作用里清空
  const [detail, setDetail] = useState<{ path: string; label: string | null }>({ path: "", label: null });
  const { pathname } = useLocation();
  const set = useCallback((label: string | null) => setDetail({ path: window.location.pathname, label }), []);
  return (
    <DetailContext.Provider value={set}>
      <DetailLabel.Provider value={detail.path === pathname ? detail.label : null}>{children}</DetailLabel.Provider>
    </DetailContext.Provider>
  );
}

const DetailLabel = createContext<string | null>(null);

/** 详情页把自己的名字（如作业 ID、账号邮箱）放进面包屑末尾。 */
export function useCrumb(label: string | null | undefined) {
  const set = useContext(DetailContext);
  useEffect(() => {
    set(label ?? null);
  }, [label, set]);
}

export function Crumbs() {
  const { pathname } = useLocation();
  const detail = useContext(DetailLabel);
  const items = crumbsFor(pathname);
  if (detail) items.push({ label: detail });
  return (
    <StyledBreadcrumbs aria-label="面包屑" separator={<NavigateNextRoundedIcon fontSize="small" />}>
      {items.map((c, i) => {
        const last = i === items.length - 1;
        if (c.to && !last) {
          return (
            <Link key={c.label} component={RouterLink} to={c.to} underline="hover" color="text.secondary" variant="caption">
              {c.label}
            </Link>
          );
        }
        return (
          <Typography
            key={c.label}
            variant="caption"
            sx={last ? { color: "text.primary", fontWeight: 600 } : { color: "text.secondary" }}
            noWrap
          >
            {c.label}
          </Typography>
        );
      })}
    </StyledBreadcrumbs>
  );
}
